import React, { useEffect, useState, useMemo } from 'react';

import { Box } from '@mui/system';

import {
  IPostedShiftsData,
  PharmacistLegendVariant,
  getPostedShifts,
  pharmacistActions,
  postedShifts,
  resetPostedShifts,
  resetStatus,
  successSelector,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { resetDialog } from '../../../../reducers/dialogReducer';
import DynamicTable from '../../../DynamicTable/DynamicTable';
import strings from '../../../../localization';
import { ReactComponent as CloseBtn } from '../../../../assets/svg/closeBtn.svg';
import WorkshiftTitle from '../WorkshiftTitle';
import { renderScreen, setOpen } from '../../../../actions/drawerActions';
import { ScreenTypes } from '../../../../helpers/Constants';
import NoAvailabilities from '../../../Admin/MatchingAvailabilities/NoAvailabilities';

import { headers, rowsMapper } from './helpers';
import classes from './styles';
import useGetItems from '../../../../hooks/useGetItems';

interface IInfoProps {
  id: string;
}

const PharmacyPostedList = ({ id }: IInfoProps) => {
  const dispatch = useAppDispatch();

  const { getImage } = useGetItems();

  const [initialShiftsList, setInitialShiftsList] = useState<
    Array<IPostedShiftsData>
  >([]);

  const postedShiftsObj = useAppSelector(postedShifts);
  const postedShiftsList = postedShiftsObj?.data;
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const style = classes('common.gray');

  const isInitialShiftsListEmpty = initialShiftsList.length === 0;

  const updatedPostedShiftsList: Array<IPostedShiftsData> = useMemo(() => {
    if (isInitialShiftsListEmpty) {
      return postedShiftsList;
    }

    const updatedList: Array<IPostedShiftsData> = initialShiftsList.map(
      (innerPostedShiftItem: IPostedShiftsData) => {
        const postedShiftItemExists = postedShiftsList.some(
          (postedShiftItem) =>
            postedShiftItem?.workShiftId === innerPostedShiftItem?.workShiftId,
        );

        if (postedShiftItemExists) {
          return { ...innerPostedShiftItem, requested: false };
        }

        return { ...innerPostedShiftItem, requested: true };
      },
    );

    return updatedList;
  }, [initialShiftsList, postedShiftsList, isInitialShiftsListEmpty]);

  const isPostShiftRequestDone = useAppSelector((state) =>
    successSelector([pharmacistActions.requestBooking], state));

  const loadSuccess = useAppSelector((state) =>
    successSelector([pharmacistActions.getPostedShifts], state));

  // THE PROMPT ONLY REPLACES THE TABLE ONCE THE SHIFTS HAVE ACTUALLY LOADED
  const emptyContainerComponent = loadSuccess
    && updatedPostedShiftsList.length === 0 ? (
      <NoAvailabilities message={strings.noDataAvailable} />
    ) : null;

  const closeDialog = () =>
    dispatch(resetDialog());

  const isBookingRequestButtonDisabled = (workShiftId: string) =>
    updatedPostedShiftsList.some((workShiftItem) => {
      if (workShiftItem?.workShiftId === workShiftId) {
        return workShiftItem?.requested;
      }
      return false;
    });

  const onBookingRequestButtonPressed = (workShiftId: string) =>
    () => {
      dispatch(setOpen(true));
      dispatch(resetDialog());
      dispatch(renderScreen({
        screenType: ScreenTypes.serviceWorkshiftDetails,
        screenNumber: 1,
        eventIds: [workShiftId],
        type: PharmacistLegendVariant.workshift,
      }));
    };

  const buttonConfig = (workShiftId: string) => {
    const label = isBookingRequestButtonDisabled(workShiftId)
      ? strings.requested
      : strings.requestBooking;
    const buttonStyle = isBookingRequestButtonDisabled(workShiftId)
      ? style.requestedButton
      : style.requestButton;

    return {
      variant: 'outlined',
      label,
      style: buttonStyle,
      disabled: isBookingRequestButtonDisabled(workShiftId),
      handlePress: onBookingRequestButtonPressed(workShiftId),
    };
  };

  const Table = {
    title: '',
    headers: headers(),
    rows: rowsMapper(
      updatedPostedShiftsList,
      timeFormat,
      dispatch,
      buttonConfig,
    ),
    headerBar: [],
  };

  useEffect(() => {
    if (isInitialShiftsListEmpty) {
      setInitialShiftsList(postedShiftsList);
    }
  }, [postedShiftsList, isInitialShiftsListEmpty]);

  useEffect(() => {
    dispatch(getPostedShifts(id));

    return () => {
      dispatch(resetPostedShifts());
      dispatch(resetStatus([pharmacistActions.getPostedShifts]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPostShiftRequestDone) {
      dispatch(getPostedShifts(id));
      dispatch(resetStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostShiftRequestDone]);

  return (
    <Box>
      <WorkshiftTitle
        image={getImage(postedShiftsObj?.logoId)}
        title={strings.singlePostedWorkshift}
        onClose={closeDialog}
        CloseBtn={CloseBtn}
        customStyle={style}
      />

      <DynamicTable
        table={Table}
        loadSuccess
        hidePagination
        emptyContainerComponent={emptyContainerComponent}
      />
    </Box>
  );
};

export default PharmacyPostedList;
